"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { GuidedSubmitButton } from "@/components/forms/GuidedSubmitButton";

type Application = { status: "pending" | "contracting" | "verified" | "rejected"; method: string; companyName: string; reason: string };
type Props = { onVerified?: () => void; preview?: "pending" | "passed" | "failed"; unavailable?: boolean };

export function EnterpriseVerificationForm({ onVerified, preview, unavailable = false }: Props) {
  const [application, setApplication] = useState<Application | null>(preview === "passed" ? { status: "verified", method: "manual", companyName: "", reason: "" } : null);
  const [loaded, setLoaded] = useState(Boolean(preview));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(preview === "failed" ? "资料未通过审核，请核对营业执照后重新提交。" : "");
  const [confirmed, setConfirmed] = useState(false);
  const [file, setFile] = useState<File>();
  const submission = useRef("");
  const callback = useRef(onVerified);
  useEffect(() => { callback.current = onVerified; }, [onVerified]);
  useEffect(() => { if (application?.status === "rejected") submission.current = ""; }, [application]);
  useEffect(() => {
    if (preview || unavailable) return;
    const controller = new AbortController();
    fetch("/api/onboarding/enterprise", { cache: "no-store", signal: controller.signal }).then(async response => {
      const value = await response.json();
      if (!response.ok) throw new Error(value.error || "企业认证状态读取失败");
      setApplication(value.application); setLoaded(true);
    }).catch(error => { if (!controller.signal.aborted) setMessage(error instanceof Error ? error.message : "企业认证状态读取失败"); });
    return () => controller.abort();
  }, [preview, unavailable]);
  useEffect(() => {
    if (preview || unavailable || (application?.status !== "pending" && application?.status !== "contracting")) return;
    const controller = new AbortController();
    let checking = false;
    const timer = setInterval(async () => {
      if (checking || document.visibilityState !== "visible") return;
      checking = true;
      try {
        const response = await fetch("/api/onboarding/enterprise", { cache: "no-store", signal: controller.signal });
        const value = await response.json();
        if (!response.ok || controller.signal.aborted) return;
        setApplication(value.application);
        if (value.application?.status === "verified") callback.current?.();
      } catch { /* Retry while the application is awaiting review. */ }
      finally { checking = false; }
    }, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [preview, unavailable, application?.status]);
  async function refresh() {
    if (preview) return;
    setBusy(true); setMessage("");
    try {
      const response = await fetch("/api/onboarding/enterprise", { cache: "no-store" });
      const value = await response.json();
      if (!response.ok) throw new Error(value.error || "企业认证状态读取失败");
      setApplication(value.application); setLoaded(true);
      callback.current?.();
    } catch (error) { setMessage(error instanceof Error ? error.message : "企业认证状态读取失败"); }
    finally { setBusy(false); }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (preview) { setMessage("此入口不提交企业资料，请通过上方状态切换继续。"); return; }
    if (busy || !loaded || unavailable || !confirmed || !file) return;
    const fields = new FormData(event.currentTarget);
    setBusy(true); setMessage("");
    try {
      if (!submission.current) submission.current = crypto.randomUUID().replaceAll("-", "");
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(new Error("营业执照读取失败，请重新选择"));
        reader.readAsDataURL(file);
      });
      const response = await fetch("/api/onboarding/enterprise", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
        submissionId: submission.current, companyName: fields.get("companyName"), creditCode: fields.get("creditCode"),
        legalPerson: fields.get("legalPerson"), contactPhone: fields.get("contactPhone"), licenseBase64: base64, confirmed: true,
      }) });
      const value = await response.json();
      if (!response.ok) throw new Error(value.error || "提交未完成，请重试读取状态后确认结果");
      setApplication(value.application); setFile(undefined);
    } catch (error) { setMessage(error instanceof Error ? error.message : "提交未完成，请重试读取状态后确认结果"); }
    finally { setBusy(false); }
  }
  const readOnly = busy || unavailable;
  const waiting = application?.status === "pending" || application?.status === "contracting";
  return <div className="rg-enterprise">
    {application?.status === "verified" ? <p className="rg-status" role="status">企业认证已通过</p> : waiting ? <div className="rg-status" role="status">
      <strong>{application.companyName}</strong><span>{application.method === "manual" ? "已提交，等待平台人工审核" : "原企业认证申请处理中，请联系平台核对"}</span>
    </div> : <>
      <form className="rg-form" onSubmit={submit}>
        <div className="rg-enterprise-fields">
          <label className="rg-field">企业名称（必填）<input name="companyName" required maxLength={160} placeholder="与营业执照一致" disabled={readOnly} /></label>
          <label className="rg-field">统一社会信用代码（必填）<input name="creditCode" required minLength={18} maxLength={18} placeholder="18 位统一社会信用代码" autoCapitalize="characters" disabled={readOnly} /></label>
          <label className="rg-field">法定代表人（必填）<input name="legalPerson" required maxLength={60} placeholder="营业执照登记的法定代表人" disabled={readOnly} /></label>
          <label className="rg-field">经办人联系电话（必填）<input name="contactPhone" required inputMode="tel" autoComplete="tel" pattern="1[0-9]{10}" maxLength={11} placeholder="11 位手机号码" disabled={readOnly} /></label>
        </div>
        <label className="rg-field rg-license-upload">营业执照（必填）
          <span>上传清晰、完整的营业执照，支持 JPG、PNG、WebP，最大 2 MB</span>
          <input aria-label="上传营业执照" type="file" required accept="image/jpeg,image/png,image/webp" disabled={readOnly} onChange={event => {
            const selected = event.target.files?.[0];
            if (selected && (!["image/jpeg", "image/png", "image/webp"].includes(selected.type) || selected.size > 2 * 1024 * 1024)) {
              setMessage("请上传不超过 2 MB 的 JPG、PNG 或 WebP 营业执照图片"); event.target.value = ""; setFile(undefined); return;
            }
            setFile(selected); setMessage("");
          }} />
        </label>
        <label className="rg-consent"><input type="checkbox" checked={confirmed} disabled={readOnly} onChange={event => setConfirmed(event.target.checked)} /><span>我确认资料真实有效，已获企业授权办理本次认证。</span></label>
        {application?.status === "rejected" && <p className="rg-error" role="alert">审核未通过：{application.reason}</p>}
        <GuidedSubmitButton type="submit" className="rg-primary" disabled={readOnly} reason={!preview && !loaded ? "认证状态尚未读取，请稍候或重试。" : !file ? "请上传营业执照。" : !confirmed ? "请确认资料真实有效及经办授权。" : ""}>{busy ? "正在提交…" : "下一步"}</GuidedSubmitButton>
      </form>
    </>}
    {message && <p role="alert" className="rg-error">{message}</p>}
    {!preview && message && <button type="button" className="rg-refresh" disabled={busy || unavailable} onClick={() => void refresh()}>{busy ? "正在读取…" : "重试读取状态"}</button>}
  </div>;
}
